import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { StatusBar } from "../components/Shell";
import Ico from "../components/Ico";
import { useAppStore } from "../components/AppStore";
import { chores, neighbors, currentUser } from "../mock";
import { ChevronLeft, Star, DollarSign, Clock, MapPin, CheckCircle2, ShieldCheck, ChevronRight, Sparkles, X } from "lucide-react";

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { requestJob, statusOf } = useAppStore();
  const chore = chores.find((c) => c.id === id) || chores[0];
  const poster = neighbors[chore.poster];
  const [slot, setSlot] = useState(chore.slots[0]);
  const [showBg, setShowBg] = useState(false);
  const [sent, setSent] = useState(false);
  const status = statusOf(chore.id);

  const accept = () => {
    requestJob(chore.id, slot);
    setSent(true);
  };

  return (
    <div className="gd-screen bg-[#eef1fb]">
      {/* colored header */}
      <div className="flex-shrink-0 px-5 pb-14 pt-0" style={{ background: `linear-gradient(160deg,${chore.tint},${shade(chore.tint)})`, borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
        <StatusBar dark={false} />
        <div className="mt-1 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="gd-tap grid h-10 w-10 place-items-center rounded-full bg-white/20">
            <ChevronLeft size={20} color="#fff" />
          </button>
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/25">
            <Ico name={chore.icon} size={24} color="#fff" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="rounded-full bg-white/25 px-2.5 py-1 text-[10px] font-bold text-white">{chore.category}</span>
          {chore.urgent && <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-extrabold" style={{ color: "#e97f0c" }}>URGENT</span>}
        </div>
        <h1 className="mt-2 text-3xl font-black text-white">{chore.title}</h1>
        <div className="mt-1 flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} fill={i < Math.round(chore.rating) ? "#fff" : "none"} color="#fff" />)}
          <span className="ml-1 text-xs font-semibold text-white/90">{chore.rating.toFixed(1)} · {chore.reviews} reviews</span>
        </div>
      </div>

      <div className="gd-scroll -mt-8 px-4 pb-40 gd-stagger">
        {/* stat cards */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard icon={DollarSign} tint="#4fae3f" value={`$${chore.pay}`} label="Pay" />
          <StatCard icon={Clock} tint="#3b57c9" value={chore.duration} label="Duration" />
          <StatCard icon={MapPin} tint="#f5911e" value={chore.distance} label="Distance" />
        </div>

        <div className="mt-3 flex items-center justify-center gap-2 rounded-2xl bg-[#efe9fb] py-3 text-sm font-bold text-[#8b5cf6]">
          <Sparkles size={16} /> Complete this task to earn +{chore.xp} XP
        </div>

        {/* description */}
        <Section title="What You Need To Do">
          <p className="text-[13px] leading-relaxed text-[#5b648a]">{chore.description}</p>
          <div className="mt-3 space-y-2">
            {chore.checklist.map((it) => (
              <div key={it} className="flex items-center gap-2 text-[13px] font-medium text-[#3a4468]">
                <CheckCircle2 size={16} className="text-[#4fae3f]" /> {it}
              </div>
            ))}
          </div>
        </Section>

        {/* dates */}
        <Section title="Available Dates">
          <div className="flex flex-col gap-2">
            {chore.slots.map((s) => (
              <button key={s} onClick={() => setSlot(s)} className="flex items-center justify-between rounded-2xl border-2 px-4 py-3 text-sm font-bold transition-all"
                style={{ borderColor: slot === s ? "#3b57c9" : "#e6e9f5", background: slot === s ? "#eef2ff" : "#fff", color: slot === s ? "#2b3f96" : "#5b648a" }}>
                {s}
                {slot === s && <CheckCircle2 size={18} className="text-[#3b57c9]" />}
              </button>
            ))}
          </div>
        </Section>

        {/* verified poster - SECURITY */}
        <Section title="Posted By">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl font-extrabold text-white" style={{ background: poster.color }}>{poster.initial}</div>
            <div className="flex-1">
              <p className="text-sm font-extrabold text-[#1f2a52]">{poster.name}</p>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] font-semibold text-[#9aa3c0]">
                <span className="flex items-center gap-0.5"><Star size={11} fill="#f5b301" color="#f5b301" /> {poster.rating.toFixed(1)}</span>
                <span>{poster.jobsPosted} jobs posted</span>
              </div>
            </div>
          </div>
          <button onClick={() => setShowBg(true)} className="gd-tap mt-3 flex w-full items-center justify-between rounded-2xl bg-[#eaf6ea] px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-extrabold text-[#2e7d27]">
              <ShieldCheck size={18} /> Background-Check Verified
            </span>
            <ChevronRight size={18} className="text-[#4fae3f]" />
          </button>
          <p className="mt-2 text-[11px] leading-relaxed text-[#9aa3c0]">
            Every poster passes a {poster.backgroundCheck.provider} background check before any job goes live.
          </p>
        </Section>

        {/* reviews */}
        <Section title="Neighbor's Reviews">
          <div className="space-y-3">
            {poster.reviews.map((r, i) => (
              <div key={i} className="rounded-2xl bg-[#f7f8fd] p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-bold text-[#1f2a52]">{r.name}</span>
                  <span className="flex">{Array.from({ length: r.stars }).map((_, k) => <Star key={k} size={12} fill="#f5b301" color="#f5b301" />)}</span>
                </div>
                <p className="mt-1 text-[12px] leading-relaxed text-[#5b648a]">{r.text}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* sticky CTA */}
      <div className="absolute inset-x-0 bottom-0 bg-white/90 px-5 pb-6 pt-4 backdrop-blur" style={{ boxShadow: "0 -8px 24px rgba(31,42,82,.08)" }}>
        {status === "approved" ? (
          <button className="w-full rounded-2xl py-4 text-base font-extrabold text-white" style={{ background: "#4fae3f" }}>
            ✓ Parent-Approved — Ready to Start
          </button>
        ) : status === "pending" ? (
          <button onClick={() => navigate(`/approval/${chore.id}`)} className="gd-orange-btn w-full rounded-2xl py-4 text-base font-extrabold">
            Awaiting Parent Approval →
          </button>
        ) : (
          <button onClick={accept} className="gd-blue-btn w-full rounded-2xl py-4 text-base font-extrabold">
            Request to Accept · {slot.split(" · ")[0]}
          </button>
        )}
        <p className="mt-2 text-center text-[11px] font-semibold text-[#9aa3c0]">A parent must approve before you can start</p>
      </div>

      {/* background check sheet */}
      {showBg && <BgSheet poster={poster} onClose={() => setShowBg(false)} />}
      {/* request sent sheet */}
      {sent && <SentSheet chore={chore} onView={() => navigate(`/approval/${chore.id}`)} onClose={() => { setSent(false); navigate("/home"); }} />}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="gd-card mt-3 p-4">
      <h3 className="mb-2 text-sm font-extrabold text-[#1f2a52]">{title}</h3>
      {children}
    </div>
  );
}
function StatCard({ icon: Icon, tint, value, label }) {
  return (
    <div className="flex flex-col items-center rounded-[18px] bg-white py-3 shadow-[0_8px_20px_rgba(31,42,82,0.06)]">
      <Icon size={18} color={tint} />
      <span className="mt-1 text-base font-black text-[#1f2a52]">{value}</span>
      <span className="text-[10px] font-semibold text-[#9aa3c0]">{label}</span>
    </div>
  );
}

function BgSheet({ poster, onClose }) {
  const bg = poster.backgroundCheck;
  return (
    <div className="absolute inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div className="gd-enter w-full rounded-t-[28px] bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-[#eaf6ea]"><ShieldCheck size={26} className="text-[#4fae3f]" /></div>
          <div>
            <h3 className="text-lg font-black text-[#1f2a52]">Background-Check Verified</h3>
            <p className="text-xs font-semibold text-[#9aa3c0]">Cleared by {bg.provider} · {bg.clearedOn}</p>
          </div>
        </div>
        <div className="mt-4 space-y-2.5">
          {bg.items.map((it) => (
            <div key={it} className="flex items-center gap-2.5 rounded-2xl bg-[#f7f8fd] px-4 py-3 text-sm font-semibold text-[#3a4468]">
              <CheckCircle2 size={18} className="text-[#4fae3f]" /> {it}
            </div>
          ))}
        </div>
        <p className="mt-4 rounded-2xl bg-[#eef2ff] p-3 text-[11px] leading-relaxed text-[#5b648a]">
          Posters cannot list a job until this check is complete. This is how GEN-DO keeps predators off the platform.
        </p>
        <button onClick={onClose} className="gd-blue-btn mt-4 w-full rounded-2xl py-3.5 font-extrabold">Got it</button>
      </div>
    </div>
  );
}

function SentSheet({ chore, onView, onClose }) {
  return (
    <div className="absolute inset-0 z-50 flex items-end bg-black/40">
      <div className="gd-enter relative w-full rounded-t-[28px] bg-white p-6 pb-8">
        <button onClick={onClose} className="absolute right-5 top-5 text-[#a3abc9]"><X size={20} /></button>
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
        <div className="grid place-items-center">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-[#eef2ff]"><ShieldCheck size={32} className="text-[#3b57c9]" /></div>
          <h3 className="mt-3 text-xl font-black text-[#1f2a52]">Sent for Parent Approval</h3>
          <p className="mt-1 max-w-[280px] text-center text-sm text-[#5b648a]">
            Your request for <b>{chore.title}</b> was sent to <b>{currentUser.parent}</b>. You can start once it's approved.
          </p>
        </div>
        <button onClick={onView} className="gd-orange-btn mt-5 w-full rounded-2xl py-3.5 font-extrabold">View Parent Approval (demo) →</button>
        <button onClick={onClose} className="mt-2 w-full rounded-2xl py-3 font-bold text-[#5b648a]">Back to Home</button>
      </div>
    </div>
  );
}

function shade(hex) {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) - 30, g = ((n >> 8) & 255) - 30, b = (n & 255) - 30;
  r = Math.max(0, r); g = Math.max(0, g); b = Math.max(0, b);
  return `rgb(${r},${g},${b})`;
}
